/**
 * workflow-validation.ts
 *
 * Rule-based, non-blocking workflow checks surfaced as warnings/recommendations.
 *
 * Adding a check = append one entry to the RULES registry below. Tunable data
 * (event/state names, integration detection, PAY/EXPIRE pair) lives in
 * workflow-validation.config.ts so rules can be adjusted without editing logic.
 *
 * Behaviour is grounded in the application engine:
 *  - "ANY" start state matches any current state.
 *  - async INTEGRATION calls back via a PUSH event (app waits); without a PUSH
 *    transition from that state the application gets stuck.
 *  - sync INTEGRATION (async=false) returns immediately but does NOT auto-advance,
 *    so the state needs a system-run nextEvent or the application gets stuck.
 */
import {
  IAction,
  IBuilderModel,
  ITransition,
} from '../models/workflow-builder.model';
import {
  IWorkflowValidationConfig,
  WORKFLOW_VALIDATION_CONFIG,
} from './workflow-validation.config';

export type WarningSeverity = 'warning' | 'info';

export interface Warning {
  kind:
    | 'dead-end'
    | 'unreachable'
    | 'ambiguous'
    | 'missing-expire-payment'
    | 'unauthorised'
    | 'system-event-no-trigger'
    | 'next-event-missing'
    | 'next-event-not-system'
    | 'next-event-stuck'
    | 'async-integration-no-push'
    | 'async-integration-push-loop'
    | 'sync-integration-no-next-event'
    | 'sync-integration-has-push'
    | 'integration-prefer-async';
  /** 'warning' (default) = likely broken; 'info' = a recommendation, not an error. */
  severity?: WarningSeverity;
  stateCode?: string;
  tid?: string;
  message: string;
}

/** An event is authorised if the system performs it OR at least one role may trigger it. */
export function isAuthorised(t: ITransition): boolean {
  const a = t.authorisation;
  return (
    !!a &&
    (a.systemAuthorised === true ||
      (Array.isArray(a.authorisedRoles) && a.authorisedRoles.length > 0))
  );
}

// ─── shared helpers ──────────────────────────────────────────────────────────

interface EndStateRef {
  stateCode: string;
  nextEvent: string | null;
  actions: IAction[];
}

interface RuleContext {
  cfg: IWorkflowValidationConfig;
  transitions: ITransition[];
  allCodes: Set<string>;
  inbound: Set<string>;
  endStates: EndStateRef[];
}

interface WorkflowRule {
  /** Descriptive id for the rule (a rule may emit more than one Warning kind). */
  id: string;
  run(ctx: RuleContext): Warning[];
}

function isIntegration(a: IAction, cfg: IWorkflowValidationConfig): boolean {
  return a.actionType.startsWith(cfg.integrationActionPrefix);
}

function isAsync(a: IAction, cfg: IWorkflowValidationConfig): boolean {
  const rawArgs = (a.raw?.['args'] as Record<string, unknown>) ?? null;
  return (
    a.args?.[cfg.asyncArgKey] === true || rawArgs?.[cfg.asyncArgKey] === true
  );
}

function buildContext(model: IBuilderModel): RuleContext {
  const cfg = WORKFLOW_VALIDATION_CONFIG;
  const transitions = model.transitions;
  const allCodes = new Set<string>([cfg.startState]);
  const inbound = new Set<string>();
  const endStates: EndStateRef[] = [];
  for (const t of transitions) {
    allCodes.add(t.startState);
    for (const es of [t.one, t.two]) {
      if (!es) continue;
      allCodes.add(es.stateCode);
      inbound.add(es.stateCode);
      endStates.push({
        stateCode: es.stateCode,
        nextEvent: es.nextEvent ?? null,
        actions: es.actions ?? [],
      });
    }
  }
  return { cfg, transitions, allCodes, inbound, endStates };
}

// ─── rules registry (append here to add a check) ─────────────────────────────

const RULES: WorkflowRule[] = [
  // Connectivity: a state must be landed on by some transition (be an endState),
  // otherwise nothing leads into it. NOT a BFS from NEW — states entered via async
  // callbacks would be false-flagged. NEW is the entry; ANY is a wildcard start.
  {
    id: 'connectivity',
    run: ({ cfg, allCodes, inbound }) => {
      const out: Warning[] = [];
      for (const code of allCodes) {
        if (code === cfg.startState || code === cfg.anyState) continue;
        if (!inbound.has(code)) {
          out.push({
            kind: 'unreachable',
            severity: 'warning',
            stateCode: code,
            message: `State "${code}" is not connected — no event leads into it (it is never an end state of any transition).`,
          });
        }
      }
      return out;
    },
  },

  // Ambiguous: same (startState, event) more than once with no guard to choose.
  {
    id: 'ambiguous',
    run: ({ transitions }) => {
      const out: Warning[] = [];
      const byKey = new Map<string, ITransition[]>();
      for (const t of transitions) {
        const key = `${t.startState} ${t.event}`;
        const group = byKey.get(key) ?? [];
        if (group.length === 0) byKey.set(key, group);
        group.push(t);
      }
      for (const group of byKey.values()) {
        if (
          group.length > 1 &&
          group.every(t => !(t.guard && t.guard.clauses.length > 0))
        ) {
          out.push({
            kind: 'ambiguous',
            severity: 'warning',
            stateCode: group[0].startState,
            message: `Event "${group[0].event}" leaves "${group[0].startState}" more than once without a guard to choose between them.`,
          });
        }
      }
      return out;
    },
  },

  // Authorisation: every event must be system-performed or role-gated.
  {
    id: 'authorisation',
    run: ({ transitions }) => {
      const out: Warning[] = [];
      for (const t of transitions) {
        if (!isAuthorised(t)) {
          out.push({
            kind: 'unauthorised',
            severity: 'warning',
            stateCode: t.startState,
            tid: t.id,
            message: `Event "${t.event}" from "${t.startState}" has no authorised role and is not system-performed.`,
          });
        }
      }
      return out;
    },
  },

  // nextEvent wiring: system events must be triggered (except PUSH callbacks), and
  // every nextEvent must resolve to a system event from the landed state.
  { id: 'next-event-wiring', run: checkNextEventWiring },

  // async INTEGRATION must have a callback (PUSH) event from that state.
  {
    id: 'async-integration',
    run: ({ cfg, transitions, endStates }) => {
      const out: Warning[] = [];
      const statesWithCallback = new Set(
        transitions
          .filter(t => cfg.callbackEvents.includes(t.event))
          .map(t => t.startState)
      );
      const statesWithLoopingCallback = new Set(
        transitions
          .filter(
            t =>
              cfg.callbackEvents.includes(t.event) &&
              [t.one, t.two]
                .filter(Boolean)
                .every(outcome => outcome?.stateCode === t.startState)
          )
          .map(t => t.startState)
      );
      const flagged = new Set<string>();
      for (const es of endStates) {
        if (flagged.has(es.stateCode)) continue;
        const hasAsyncIntegration = es.actions.some(
          a => isIntegration(a, cfg) && isAsync(a, cfg)
        );
        if (hasAsyncIntegration && !statesWithCallback.has(es.stateCode)) {
          flagged.add(es.stateCode);
          out.push({
            kind: 'async-integration-no-push',
            severity: 'warning',
            stateCode: es.stateCode,
            message: `"${es.stateCode}" runs an asynchronous integration but has no ${cfg.callbackEvents[0]} event to receive its callback — the application would stay stuck.`,
          });
        } else if (
          hasAsyncIntegration &&
          statesWithLoopingCallback.has(es.stateCode)
        ) {
          flagged.add(es.stateCode);
          out.push({
            kind: 'async-integration-push-loop',
            severity: 'warning',
            stateCode: es.stateCode,
            message: `"${es.stateCode}" receives an asynchronous integration callback but the ${cfg.callbackEvents[0]} event returns to the same state — choose a different completed state.`,
          });
        }
      }
      return out;
    },
  },

  // sync INTEGRATION (async=false) needs a nextEvent to advance (no callback is sent);
  // when it IS configured, recommend async for easier retries (info, not a warning).
  {
    id: 'sync-integration',
    run: ({ cfg, transitions, endStates }) => {
      const out: Warning[] = [];
      const statesWithCallback = new Set(
        transitions
          .filter(t => cfg.callbackEvents.includes(t.event))
          .map(t => t.startState)
      );
      const warned = new Set<string>();
      const callbackWarned = new Set<string>();
      const informed = new Set<string>();
      for (const es of endStates) {
        const hasSyncIntegration = es.actions.some(
          a => isIntegration(a, cfg) && !isAsync(a, cfg)
        );
        if (!hasSyncIntegration) continue;
        if (
          statesWithCallback.has(es.stateCode) &&
          !callbackWarned.has(es.stateCode)
        ) {
          callbackWarned.add(es.stateCode);
          out.push({
            kind: 'sync-integration-has-push',
            severity: 'warning',
            stateCode: es.stateCode,
            message: `"${es.stateCode}" has a ${cfg.callbackEvents[0]} callback event but its integration is synchronous — remove the callback or mark the integration asynchronous.`,
          });
        }
        if (!es.nextEvent) {
          if (warned.has(es.stateCode)) continue;
          warned.add(es.stateCode);
          out.push({
            kind: 'sync-integration-no-next-event',
            severity: 'warning',
            stateCode: es.stateCode,
            message: `"${es.stateCode}" runs a synchronous integration but has no nextEvent to advance the application — a sync integration sends no callback, so it would stay stuck.`,
          });
        } else if (!informed.has(es.stateCode)) {
          informed.add(es.stateCode);
          out.push({
            kind: 'integration-prefer-async',
            severity: 'info',
            stateCode: es.stateCode,
            message: `"${es.stateCode}" uses a synchronous integration. Consider marking the integration asynchronous — async integrations allow easier retries.`,
          });
        }
      }
      return out;
    },
  },

  // Engine rule: a PAY path needs an EXPIRE_PAYMENT path.
  {
    id: 'pay-expire',
    run: ({ cfg, transitions }) => {
      const hasPay = transitions.some(t => t.event === cfg.payEvent);
      const hasExpire = transitions.some(
        t => t.event === cfg.expirePaymentEvent
      );
      if (hasPay && !hasExpire) {
        return [
          {
            kind: 'missing-expire-payment',
            severity: 'warning',
            message: `A ${cfg.payEvent} event exists but there is no ${cfg.expirePaymentEvent} event to handle unpaid applications.`,
          },
        ];
      }
      return [];
    },
  },
];

export function validate(model: IBuilderModel): Warning[] {
  const ctx = buildContext(model);
  return RULES.flatMap(rule => rule.run(ctx));
}

/**
 * nextEvent ↔ system-event consistency:
 *  - a system-performed event must be chained to by some end state's nextEvent
 *    (except PUSH callbacks, which are triggered externally by the integration);
 *  - a nextEvent must resolve to an event from the landed state, and that event
 *    must be system-performed (the system can't auto-run a role-gated event).
 */
function checkNextEventWiring(ctx: RuleContext): Warning[] {
  const { cfg, transitions, endStates } = ctx;
  const warnings: Warning[] = [];

  for (const t of transitions) {
    if (t.authorisation?.systemAuthorised !== true) continue;
    // PUSH is an async-integration callback triggered externally, not via a
    // nextEvent chain — validated by the async-integration rule instead.
    if (cfg.callbackEvents.includes(t.event)) continue;
    // ANY is a wildcard start: its events apply to whatever state the app is in,
    // so "no end state lands on ANY" is not a valid not-triggered signal.
    if (t.startState === cfg.anyState) continue;
    const triggered = endStates.some(
      es => es.stateCode === t.startState && es.nextEvent === t.event
    );
    if (!triggered) {
      warnings.push({
        kind: 'system-event-no-trigger',
        severity: 'warning',
        stateCode: t.startState,
        tid: t.id,
        message: `System event "${t.event}" from "${t.startState}" is never triggered — no end state that lands on "${t.startState}" chains to it via nextEvent.`,
      });
    }
  }

  const seen = new Set<string>();
  for (const es of endStates) {
    if (!es.nextEvent || seen.has(`${es.stateCode} ${es.nextEvent}`)) continue;
    seen.add(`${es.stateCode} ${es.nextEvent}`);
    const matching = transitions.filter(
      t => t.startState === es.stateCode && t.event === es.nextEvent
    );
    if (!matching.length) {
      warnings.push({
        kind: 'next-event-missing',
        severity: 'warning',
        stateCode: es.stateCode,
        message: `"${es.stateCode}" chains to nextEvent "${es.nextEvent}", but no "${es.nextEvent}" event starts from "${es.stateCode}".`,
      });
    } else if (
      !matching.some(t => t.authorisation?.systemAuthorised === true)
    ) {
      warnings.push({
        kind: 'next-event-not-system',
        severity: 'warning',
        stateCode: es.stateCode,
        message: `"${es.stateCode}" runs "${es.nextEvent}" automatically (nextEvent), but "${es.nextEvent}" is not a system event (systemAuthorised) — the system can't auto-run a role-gated event.`,
      });
    } else if (
      matching
        .filter(t => t.authorisation?.systemAuthorised === true)
        .every(t => transitionStaysOnState(t, es.stateCode))
    ) {
      warnings.push({
        kind: 'next-event-stuck',
        severity: 'warning',
        stateCode: es.stateCode,
        message: `"${es.stateCode}" chains to nextEvent "${es.nextEvent}", but that system event only returns to "${es.stateCode}" — it is not going anywhere.`,
      });
    }
  }
  return warnings;
}

function transitionStaysOnState(t: ITransition, stateCode: string): boolean {
  return [t.one, t.two]
    .filter(Boolean)
    .every(es => es?.stateCode === stateCode);
}
